import { readonly, ref } from 'vue';
import { networkPendingRequests } from '@/services/http';

/**
 * 「系统更新了就自动切到新版」——检测 + 分流 + 护栏。
 *
 * 背景：生产是 SPA，用户浏览器里挂着的旧页面不会自己更新。
 * 静态资源（含 index.html）后端都设了 `Cache-Control: no-store`，所以 `location.reload()`
 * **必定拿到新版**；`/api/version` 又是实时读 `package.json` 的版本号，天生就是一个可用的版本探针。
 *
 * 分流策略（1.1.73 起：**先亮横幅，再决定怎么刷**）：
 *   · 一检测到新版本 → 把 `updateReady` 置起来，AppShell 在**页面正上方**出可点击的升级横幅
 *     （用户点横幅 = 立即刷新）；
 *   · 用户空闲（90s 内没有键鼠操作、且没有在途请求）→ 横幅先展示 IDLE_GRACE_MS，
 *     期间没被点击就静默 reload，避免有人一直挂着旧版本；
 *   · 正在忙（在打字、在等接口、在导出…）→ 只保留横幅，等用户挑时机点，不抢刷。
 *
 * 🔴 只在生产启用（后端 `/api/version` 返回 env）：本地开发时 git 一动就 reload 没法调试。
 *
 * 不会丢的：登录态（cookie 30 天）、备货计划草稿（存 localStorage）、筛选条件里持久化的那部分。
 * 会被打断的：正在填的弹窗内容、正在跑的导出/加载 → 由上面的"忙碌判定"挡住。
 */
const CHECK_INTERVAL_MS = 3 * 60 * 1000; // 每 3 分钟探一次
const FIRST_CHECK_DELAY_MS = 60 * 1000; // 页面打开 1 分钟后先探一次（部署后能快点铺开）
const IDLE_MS = 90 * 1000; // 距最后一次操作超过 90s 才算空闲
const BUSY_RECHECK_MS = 20 * 1000; // 已经发现有新版时，多久复核一次是否空下来
const IDLE_GRACE_MS = 20 * 1000; // 空闲时：横幅先亮这么久，没人点再静默刷新

type VersionSnapshot = { version?: string; sha?: string; env?: string };
/** 有新版本待应用 → AppShell 据此在页面正上方出「升级横幅」（点击即刷新） */
const updateReady = ref(false);
/** 新版本号（用于提示文案），拿不到就空着 */
const updateLabel = ref('');

let baseline: VersionSnapshot | null = null;
let lastActivityAt = Date.now();
let checkTimer = 0;
let busyTimer = 0;
let firstTimer = 0;
/** 空闲时"横幅展示期 → 静默刷新"的定时器（0 = 没排） */
let idleTimer = 0;
let started = false;
/** 确认过"不是生产"就彻底停手（本地开发不该反复探版本） */
let disabled = false;
/** 用户点了「稍后」：本次会话不再自动刷、也不再提示（下次打开页面自然就是新版了） */
let dismissed = false;

function markActivity(): void {
  lastActivityAt = Date.now();
}

/** 🔴 故意用原生 fetch 而不是 http.request：后者会把 networkPendingRequests +1，
 *  那样"判断有没有在途请求"时永远算自己 → 永远判定为忙 → 永远不会静默刷。 */
async function readVersion(): Promise<VersionSnapshot | null> {
  try {
    const res = await fetch('/api/version', { cache: 'no-store' });
    if (!res.ok) return null;
    return (await res.json()) as VersionSnapshot;
  } catch {
    return null;
  }
}

/** 代码有没有换：就比 `package.json` 的版本号。
 *  （`/api/version` 还回了 git sha，但按约定"每次发布必改版本号"已经够用，不额外折腾；
 *   真需要更稳时把这里换成比 sha 即可。） */
function changedFromBaseline(now: VersionSnapshot | null): boolean {
  if (!baseline || !now) return false;
  return Boolean(baseline.version && now.version && baseline.version !== now.version);
}

function isBusy(): boolean {
  if (Date.now() - lastActivityAt < IDLE_MS) return true;
  if (networkPendingRequests.value > 0) return true;
  return false;
}

async function check(): Promise<void> {
  const now = await readVersion();
  if (!changedFromBaseline(now)) return;
  if (now?.version) updateLabel.value = `v${now.version}`;
  if (dismissed) return;
  // 1.1.73: 不管忙不忙，先把横幅亮起来（原来只有"忙"才提示，空闲会被立刻刷掉、用户看不到提示）
  updateReady.value = true;
  if (isBusy()) return; // 忙 → 只留横幅，等用户点
  if (idleTimer) return; // 空闲 → 已经排好静默刷新，等它到点
  idleTimer = window.setTimeout(() => {
    idleTimer = 0;
    // 展示期结束：仍空闲且没被忽略 → 静默刷到新版；若用户这时在忙，就继续等（下次复核会再排）
    if (!dismissed && !isBusy()) window.location.reload();
  }, IDLE_GRACE_MS);
}

function onVisibilityChange(): void {
  if (document.visibilityState !== 'visible') return;
  // 切回来本身算一次操作：用户刚回到页面，不该立刻被刷掉；先探一次，忙就转成提示条
  markActivity();
  void check();
}

/** 立即刷新（提示条上的按钮） */
function reloadNow(): void {
  window.location.reload();
}

/** 本次会话忽略更新（横幅右侧的「✕」）—— 连排好的静默刷新一起取消 */
function dismissUpdate(): void {
  dismissed = true;
  updateReady.value = false;
  window.clearTimeout(idleTimer);
  idleTimer = 0;
}

async function start(): Promise<void> {
  if (started || disabled) return;
  started = true; // 同步占位，挡住并发重入
  const snapshot = await readVersion();
  if (!snapshot) {
    started = false; // 这次没探到（网络抖/后端还没起来）→ 允许以后重挂时再试
    return;
  }
  // 🔴 baseline 只记**第一次**成功拿到的值：AppShell 在登录态恢复过程中会重新挂载，
  //    start() 因此可能被调用多次 —— 若每次都刷新 baseline，就会把"已经是新版"当成基准，
  //    那次更新就永远检测不到了（`??=` 保证只记第一次）。
  baseline ??= snapshot;
  // 本地开发不启用：否则改一行代码 / 提交一次就 reload，没法调试
  if (snapshot.env !== 'prod') {
    disabled = true;
    started = false;
    return;
  }
  for (const event of ['keydown', 'pointerdown', 'wheel', 'touchstart'] as const) {
    window.addEventListener(event, markActivity, { passive: true, capture: true });
  }
  document.addEventListener('visibilitychange', onVisibilityChange);
  firstTimer = window.setTimeout(() => void check(), FIRST_CHECK_DELAY_MS);
  checkTimer = window.setInterval(() => void check(), CHECK_INTERVAL_MS);
  // 已经知道有新版本，但用户还在忙 → 每隔一小会儿复核一次，等他空下来就静默刷
  busyTimer = window.setInterval(() => {
    if (updateReady.value) void check();
  }, BUSY_RECHECK_MS);
}

function stop(): void {
  window.clearTimeout(firstTimer);
  window.clearTimeout(idleTimer);
  idleTimer = 0;
  window.clearInterval(checkTimer);
  window.clearInterval(busyTimer);
  document.removeEventListener('visibilitychange', onVisibilityChange);
  started = false;
}

export function useVersionGuard() {
  return {
    updateReady: readonly(updateReady),
    updateLabel: readonly(updateLabel),
    start,
    stop,
    reloadNow,
    dismissUpdate,
  };
}
